import { TSaAssessment } from "@elixir/types";
import React, { Fragment, ReactElement, useEffect, useState } from "react";
import { PrimaryButton, QuestionnaireListRowItemUi, SecondaryButton, Spinner, TableDataCell, TableHeaderUi } from "ui";
import useGetDeveloperAuthInit from "../../../apis/parchi/use-get-developer-auth-init";
import useGetSaTemplate from "../../../apis/parchi/use-get-sa-template";
import useQueueStore from "../../../stores/queue-store";
import { EVENT_TYPES } from "../../../utils/bridges";
import { dispatch } from "../../../utils/bridges/events/dispatch";
import { onShowSAAction } from "../../../utils/bridges/methods";
import { getSettingsBodyConfig } from "../../workflow-view/view-all-queues";
import getB from "../../../utils/bridges/EkaBridge";
import { notification, Tabs } from "antd";
import { ensurePagifyLoaded } from "../../../utils/pagifySingeltonInstance";
import getPastSAOfPatient from "../../../apis/parchi/get-past-sa-of-patient";
import moment from "moment";

export let closeSaSubmissionFromList: (() => void) | null = null;

export const questionnaireTableHeaderConfig: Array<{
  key: string;
  width?: string | number;
  title: string;
  align?: "left" | "right" | "center";
}> = [
    {
      key: "questionnaires",
      title: "Questionnaires",
      width: "50%",
    },
    {
      key: "status",
      title: "Status",
      width: "30%",
    },
    {
      key: "actions",
      title: "Actions",
      width: "20%",
      align: "right",
    },
  ];

export const questionnaireTableBodyConfig = getSettingsBodyConfig(
  questionnaireTableHeaderConfig
);

export const sampleWorkflows: Array<TSaAssessment> = [
  {
    display_name: "Tester assessment",
    description: "description",
    cta: {
      params: {
        wflow_id: 3925,
        aid: null,
        doc_id: "121343",
        skip_profile: false,
      },
      pid: "332",
      title: "dsfs",
    },
  },
];

const QuestionnaireList = ({
  onFillNowClick,
}: {
  onFillNowClick: (workflow_id: number) => void;
}): ReactElement => {

  const [pastSa, setPastSa] = useState<Array<{ conversationid: string; created_at: string; wflow_wfid: string }>>([]);
  const [activeTabKey, setActiveTabKey] = useState<string>("1");

  const selectedAppointment = useQueueStore(
    (state) => state.selectedAppointment
  );

  useEffect(() => {
    if (selectedAppointment?.patient_oid) {
      getPastSAOfPatient({ patientId: selectedAppointment.patient_oid }).then((res) => {
        if (res.success && res.data) {
          setPastSa(res.data.sort((a, b) => moment(b.created_at).valueOf() - moment(a.created_at).valueOf()));
        }
      });
    }

  }, [selectedAppointment?.patient_oid]);

  const setSelectedAppointment = useQueueStore(
    (state) => state.setSelectedAppointment
  );

  const isQuestionnaireDrawerOpenedFromPad = useQueueStore(
    (state) => state.isQuestionnaireDrawerOpenedFromPad
  );
  const setIsQuestionnaireDrawerOpenedFromPad = useQueueStore(
    (state) => state.setIsQuestionnaireDrawerOpenedFromPad
  );

  const setQuestionnaireWorkflowId = useQueueStore(
    (state) => state.setQuestionnaireWorkflowId
  );

  const setShowAssessmentIds = useQueueStore(
    (state) => state.setShowAssessmentIds
  );
  const setWorkflowIdInShowResponse = useQueueStore(
    (state) => state.setWorkflowIdInShowResponse
  );

  const setOpenQuestionnaireDrawer = useQueueStore(
    (state) => state.setOpenQuestionnaireDrawer
  );

  const { data } = useGetSaTemplate({
    selectedDoctorOid: selectedAppointment?.doctor_oid,
    enabled: true,
  });
  const assessmentsList = data?.template || [];

  const { data: developerAuthInitData } = useGetDeveloperAuthInit();

  const filledWorkflows = React.useMemo(() => {
    let workflowsSet = new Set<string>();
    if (selectedAppointment?.sa_cmpl) {
      workflowsSet = new Set(selectedAppointment?.sa_cmpl);
    } else if (selectedAppointment) {
      const workflows = Object.keys(selectedAppointment.assessments || {});
      workflows.forEach((workflowId) => {
        workflowsSet.add(workflowId);
      });
    }

    return workflowsSet;
  }, [selectedAppointment]);

  const handleShowSAResponseClick = async ({
    assessmentId,
    workflowId,
  }: {
    assessmentId: string;
    workflowId: string;
  }) => {
    if (
      window.showSaSubmission &&
      developerAuthInitData?.data?.access_token &&
      developerAuthInitData.data.refresh_token
    ) {
      const printConfig = await getB().getPrintConfig?.({
        docid: selectedAppointment?.doctor_oid || "",
        clinicId: selectedAppointment?.clinic_id || "",
        pid: selectedAppointment?.patient_oid || "",
      });
      setWorkflowIdInShowResponse(Number(workflowId));
      setShowAssessmentIds([assessmentId]);
      closeSaSubmissionFromList = window.showSaSubmission({
        assessment_ids: [assessmentId],
        container_id: "sa-container",
        auth_token: developerAuthInitData.data.access_token,
        refresh_token: developerAuthInitData.data.refresh_token,
        printConfig: printConfig,
        onPrint: async (bodyHtml: string) => {
          let finalBodyHtml = bodyHtml;
          if (printConfig?.include_pt_details_in_body) {
            const ptDetailsHtml = printConfig?.pt_details_html || "";
            finalBodyHtml = `<div>${ptDetailsHtml} ${bodyHtml}</div>`;
          }
          await ensurePagifyLoaded()
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          window?.pagify?.render({
            head_html: printConfig?.head_html || "",
            body_html: finalBodyHtml,
            header_html: printConfig?.header_html || "",
            footer_html: printConfig?.footer_html || "",
            page_size: "A4",
            margin_left: printConfig?.margin_left,
            margin_right: printConfig?.margin_right,
            header_height: printConfig?.header_height,
            footer_height: printConfig?.footer_height,
            onPdfReady: async (blobUrl: string) => {
              window.open(blobUrl, "_blank");
            },
            onPdfError: (error: string) => {
              getB().track("ERROR", { on: "Pagify render", what: error });
              alert(`PDF generation failed: ${error}`);
            },
          });
        }
      });
      return;
    }
  };

  const handleShowSAClick = ({ assessmentId }: { assessmentId: string }) => {
    onShowSAAction({
      sa_id: assessmentId,
    });
    setOpenQuestionnaireDrawer(false);
    setSelectedAppointment();
    if (isQuestionnaireDrawerOpenedFromPad) {
      setIsQuestionnaireDrawerOpenedFromPad(false);
      dispatch({
        type: EVENT_TYPES.CLOSE_QUESTIONNAIRE_LIST,
      });
    }
  };

  const onViewResponseClick = async (workflowId: string): Promise<void> => {
    if (selectedAppointment) {
      const assessmentIdToShow =
        await getB().appointments.getAssementIdWorkflow(
          selectedAppointment.aid,
          workflowId
        );

      if (assessmentIdToShow?.sa_id) {
        handleShowSAResponseClick({
          assessmentId: assessmentIdToShow.sa_id,
          workflowId: workflowId,
        });
      } else if (selectedAppointment.assessments?.[workflowId]?.[0]) {
        handleShowSAResponseClick({
          assessmentId: selectedAppointment.assessments?.[workflowId]?.[0],
          workflowId: workflowId,
        });
        return;
      } else {
        notification.error({
          message: "No appointment selected",
          description: "Please select an appointment to view the summary.",
          duration: 5,
        });
      }
    }
  };

  const onViewSummaryClick = async (workflowId: string): Promise<void> => {
    if (selectedAppointment) {
      const assessmentIdToShow =
        await getB().appointments.getAssementIdWorkflow(
          selectedAppointment.aid,
          workflowId
        );

      if (assessmentIdToShow?.sa_id) {
        handleShowSAClick({
          assessmentId: assessmentIdToShow.sa_id,
        });
      } else if (selectedAppointment.assessments?.[workflowId]?.[0]) {
        const assessmentIdToShow =
          selectedAppointment.assessments?.[Number(workflowId)]?.[0];

        handleShowSAClick({
          assessmentId: assessmentIdToShow!,
        });
      } else {
        notification.error({
          message: "No appointment selected",
          description: "Please select an appointment to view the summary.",
          duration: 5,
        });
      }
      return;
    }
  };

  return (
    <div className="dr-bg-darwin-touch-neutral-100 dr-px-16 dr-py-12 dr-flex dr-flex-col overflow-hidden h-full">
      <Tabs
        defaultActiveKey="1"
        onChange={setActiveTabKey}
        activeKey={activeTabKey}
      >
        <Tabs.TabPane tab="Appointment Assessments" key="1" className="dr-h-full">
        </Tabs.TabPane>
        <Tabs.TabPane tab={`Filled Assessments (${pastSa.length})`} key="2" className="dr-h-full">

        </Tabs.TabPane>
      </Tabs>
      {activeTabKey === "1" && <div className="dr-flex dr-flex-col dr-w-full dr-border dr-border-darwin-neutral-200 dr-rounded-t-8 overflow-auto h-full">
        <TableHeaderUi headerConfig={questionnaireTableHeaderConfig} />
        <div className="dr-flex-1 dr-w-full overflow-auto bg-white">
          {assessmentsList.map((virtualItem, index) => {
            const isLastItem =
              index === assessmentsList.length - 1;
            const currRow = assessmentsList[index];
            const isLoaderRow =
              index > assessmentsList.length - 1;

            if (isLoaderRow || !currRow) {
              return (
                <div
                  key={virtualItem._id}
                  className="dr-w-full dr-flex dr-justify-center dr-items-center"
                >
                  <Spinner
                    size={12}
                    className="dr-text-darwin-primary-primary"
                  />
                </div>
              );
            }

            return (
              <div
                key={virtualItem._id}
                data-index={index}
              >
                <QuestionnaireListRowItemUi
                  questionnaire={{
                    name: currRow.meta.display.title,
                    workflowId: currRow.meta.sa_id,
                    isFilled: filledWorkflows.has(
                      currRow.meta.sa_id.toString()
                    ),
                  }}
                  rowConfig={questionnaireTableBodyConfig}
                  onFillNowClick={(workflowId) => {
                    setQuestionnaireWorkflowId(workflowId);
                    onFillNowClick(workflowId);
                  }}
                  onViewSummaryClick={() =>
                    onViewSummaryClick(currRow.meta.sa_id)
                  }
                  onViewResponseClick={() =>
                    onViewResponseClick(currRow.meta.sa_id)
                  }
                  isLastItem={isLastItem}
                />
              </div>
            );
          })}
        </div>
      </div>}
      {activeTabKey === "2" && <Fragment>
        {pastSa.length > 0 ? (
          <div className="dr-flex dr-flex-col dr-w-full dr-border dr-border-darwin-neutral-200 dr-rounded-t-8 overflow-auto h-full">
            <TableHeaderUi headerConfig={[
              {
                key: "questionnaires",
                title: "Questionnaires",
                width: "40%",
              },
              {
                key: "filled_on",
                title: "Filled On",
                width: "30%",
              },
              {
                key: "actions",
                title: "Actions",
                width: "30%",
                align: "right",
              },
            ]} />

            <div className="dr-flex-1 dr-w-full overflow-auto bg-white">
              {pastSa.map((sa) => (
                <div
                  key={sa.conversationid}
                  className="dr-bg-darwin-touch-neutral-0 dr-border-b dr-border-darwin-neutral-200 last:dr-border-0 dr-flex dr-items-center dr-justify-between"
                >
                  <TableDataCell
                    width={"40%"}
                  >
                    <div className="dr-px-4">
                      {assessmentsList.find(a => a.meta.sa_id.toString() === sa.wflow_wfid.toString())?.meta.display.title || 'Assessment'}
                    </div>
                  </TableDataCell>
                  <TableDataCell
                    width={"30%"}
                  >
                    <div className="dr-text-darwin-touch-neutral-600">
                      {moment(sa.created_at).format("DD MMM, YYYY hh:mm A")}
                    </div>
                  </TableDataCell>
                  <TableDataCell
                    width={"30%"}
                    align="right"
                  >
                    <div className="flex space-x-8">
                      <PrimaryButton
                        title="Show Summary"
                        size="medium"
                        onClick={() =>
                          handleShowSAClick({
                            assessmentId: sa.conversationid,
                          })
                        }
                      />
                      <SecondaryButton
                        title="View Responses"
                        size="medium"
                        onClick={() =>
                          handleShowSAResponseClick({
                            assessmentId: sa.conversationid,
                            workflowId: sa.wflow_wfid.toString(),
                          })
                        }
                      />
                    </div>
                  </TableDataCell>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="dr-flex dr-items-center dr-justify-center dr-h-full dr-text-darwin-touch-neutral-500">
            No filled assessments found
          </div>
        )}
      </Fragment>}

    </div>

  );
};

export default QuestionnaireList;
